import { GridInput } from '@it2go/data-grid';
import { filterFrag } from '../modules/content/service/types/filter.types';
import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { exportFrag } from './types/common.types';
import { BaseFolderExportInput } from '@libs/types/src/services/input-type/folder/base-folder.export-input';
import { FolderPrintObject } from '@libs/types/src/services/object-type/folder/folder-print-object';
import {
    FolderContractGridObject,
    FolderContractObject,
} from '@libs/types/src/services/object-type/folder/folder-contract.object';
import { FolderContractCreateInput } from '@libs/types/src/services/input-type/folder/folder-contract.create-input';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { OkObject } from '@it2go/types';
import { FolderContractUpdateInput } from '@libs/types/src/services/input-type/folder/folder-contract.update-input';
import { FolderContractPublicGridObject } from '@libs/types/src/services/object-type/folder/folder-contract-public.object';
import {
    FolderContractLeadTimeGridObject,
} from '@libs/types/src/services/object-type/folder/folder-contract-lead-time.object';
import {
    FolderContractLeadTimeCreateInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-lead-time.create-input';
import {
    FolderContractLeadTimeUpdateInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-lead-time.update-input';
import {
    FolderContractAttachmentGridObject,
} from '@libs/types/src/services/object-type/folder/folder-contract-attachment.object';
import {
    FolderContractAttachmentCreateInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-attachment.create-input';
import {
    FolderContractAttachmentUpdateInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-attachment.update-input';
import {
    FolderContractAddendumGridObject,
    FolderContractAddendumObject,
} from '@libs/types/src/services/object-type/folder/folder-contract-addendum.object';
import {
    FolderContractAddendumCreateInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-addendum.create-input';
import {
    FolderContractAddendumUpdateInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-addendum.update-input';
import {
    FolderContractOffersUpsertInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-offers.upsert-input';
import { FolderContractOffersObject } from '@libs/types/src/services/object-type/folder/folder-contract-offers.object';
import {
    FolderContractIncomeGridObject,
    FolderContractIncomeObject,
} from '@libs/types/src/services/object-type/folder/folder-contract-income.object';
import {
    FolderContractIncomeCreateInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-income.create-input';
import {
    FolderContractIncomeUpdateInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-income.update-input';
import {
    FolderContractIncomePreliminaryCalculationExportInput,
} from '@libs/types/src/services/input-type/folder/folder-contract-income-preliminary-calculation-export.input';

@Injectable({ providedIn: 'root' })
export class FolderContractsGqlService extends GqlServiceV2 {

    public getFolderContracts(input: GridInput, fragment: string) {
        return this.query<FolderContractGridObject>(
            `
                query($input: GridInput!) {
                    folderContract {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getFolderContractsPublic(input: GridInput, fragment: string) {
        return this.query<FolderContractPublicGridObject>(
            `
                query($input: GridInput!) {
                    folderContract {
                        publicFilter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createFolderContract(
        input: FolderContractCreateInput,
        fragment: string,
    ) {
        return this.mutate<FolderContractObject>(
            `
                mutation($input: FolderContractCreateInput!) {
                    folderContract {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateFolderContract(
        input: FolderContractUpdateInput,
        fragment: string,
    ) {
        return this.mutate<FolderContractObject>(
            `
                mutation($input: FolderContractUpdateInput!) {
                    folderContract {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteFolderContract(input: IdInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    folderContract {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getFolderContractLeadTime(input: GridInput, fragment: string) {
        return this.query<FolderContractLeadTimeGridObject>(
            `
                query($input: GridInput!) {
                    folderContractLeadTime {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createFolderContractLeadTime(input: FolderContractLeadTimeCreateInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderContractLeadTimeCreateInput!) {
                    folderContractLeadTime {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateFolderContractLeadTime(input: FolderContractLeadTimeUpdateInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderContractLeadTimeUpdateInput!) {
                    folderContractLeadTime {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteFolderContractLeadTime(input: IdInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    folderContractLeadTime {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getFolderContractAttachment(input: GridInput, fragment: string) {
        return this.query<FolderContractAttachmentGridObject>(
            `
                query($input: GridInput!) {
                    folderContractAttachment {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createFolderContractAttachment(input: FolderContractAttachmentCreateInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderContractAttachmentCreateInput!) {
                    folderContractAttachment {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateFolderContractAttachment(input: FolderContractAttachmentUpdateInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderContractAttachmentUpdateInput!) {
                    folderContractAttachment {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteFolderContractAttachment(input: IdInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    folderContractAttachment {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getFolderContractAddendum(input: GridInput, fragment: string) {
        return this.query<FolderContractAddendumGridObject>(
            `
                query($input: GridInput!) {
                    folderContractAddendum {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createFolderContractAddendum(input: FolderContractAddendumCreateInput, fragment: string) {
        return this.mutate<FolderContractAddendumObject>(
            `
                mutation($input: FolderContractAddendumCreateInput!) {
                    folderContractAddendum {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateFolderContractAddendum(input: FolderContractAddendumUpdateInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderContractAddendumUpdateInput!) {
                    folderContractAddendum {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteFolderContractAddendum(input: IdInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    folderContractAddendum {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getFolderContractIncome(input: GridInput, fragment: string) {
        return this.query<FolderContractIncomeGridObject>(
            `
                query($input: GridInput!) {
                    folderContractIncome {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public createFolderContractIncome(input: FolderContractIncomeCreateInput, fragment: string) {
        return this.mutate<FolderContractIncomeObject>(
            `
                mutation($input: FolderContractIncomeCreateInput!) {
                    folderContractIncome {
                        create(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public updateFolderContractIncome(input: FolderContractIncomeUpdateInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderContractIncomeUpdateInput!) {
                    folderContractIncome {
                        update(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public deleteFolderContractIncome(input: IdInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    folderContractIncome {
                        delete(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public upsertContractOffers(input: FolderContractOffersUpsertInput, fragment: string) {
        return this.mutate<FolderContractOffersObject>(
            `
            mutation($input: FolderContractOffersUpsertInput!) {
                folderContract {
                    upsertOffers(input: $input) {
                        ${fragment}
                    }
                }
            }
        `,
            { input },
        );
    }

    public getContractOffers(input: IdInput, fragment: string) {
        return this.query<FolderContractOffersObject>(
            `
            query($input: IdInput!) {
                folderContract {
                    getContractOffers(input: $input) {
                        ${fragment}
                    }
                }
            }
        `,
            { input },
        );
    }

    public exportFolderContract(input: BaseFolderExportInput) {
        return this.query<FolderPrintObject>(
            `
                query($input: BaseFolderExportInput!) {
                    folderContract {
                        export(input: $input) {
                            ${exportFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public exportFolderContractAttachment(input: BaseFolderExportInput) {
        return this.query<FolderPrintObject>(
            `
                query($input: BaseFolderExportInput!) {
                    folderContractAttachment {
                        export(input: $input) {
                            ${exportFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public exportFolderContractAddendum(input: BaseFolderExportInput) {
        return this.query<FolderPrintObject>(
            `
                query($input: BaseFolderExportInput!) {
                    folderContractAddendum {
                        export(input: $input) {
                            ${exportFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public exportContractIncomePreliminaryCalculation(input: FolderContractIncomePreliminaryCalculationExportInput) {
        return this.query<FolderPrintObject>(
            `
                query($input: FolderContractIncomePreliminaryCalculationExportInput!) {
                    folderContractIncome {
                        exportPreliminaryCalculation(input: $input) {
                            ${exportFrag}
                        }
                    }
                }
            `,
            { input },
        );
    }
}
